import { useEffect, useRef, useState } from "react";
import type { EventProps } from "react-big-calendar";
import dayjs from "dayjs";
import { IconUserCog } from "@tabler/icons-react";
import { Stack, Text, HoverCard } from "@mantine/core";

interface EventoPersonalizadoProps extends EventProps<any> {
  view: string;
}

const ALTURA_MINIMA_PARA_INFO = 45;

const EventoPersonalizado: React.FC<EventoPersonalizadoProps> = ({ event, view }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mostrarSoloId, setMostrarSoloId] = useState(false);

  const horaInicio = dayjs(event.start).format("HH:mm");
  const horaFin = dayjs(event.end).format("HH:mm");

  useEffect(() => {
    if (!ref.current || view === "agenda") return;

    requestAnimationFrame(() => {
      if (!ref.current) return;
      const altura = ref.current.offsetHeight;
      setMostrarSoloId(altura < ALTURA_MINIMA_PARA_INFO);
    });
  }, [event, view]);

  // 🔹 Componente de datos reutilizable
  const DatosEvento = ({ incluirTecnico }: { incluirTecnico: boolean }) => (
    <Stack gap={2}>
      <Text fz={11}><strong>{event.Id}</strong></Text>
      <Text fz={11}><strong>Tipo:</strong> {event.TipoTarea?.Nombre}</Text>
      <Text fz={11}><strong>Hora:</strong> {horaInicio} - {horaFin}</Text>
      <Text fz={11}><strong>Estado:</strong> {event.EstadoServicio?.Nombre}</Text>
      <Text fz={11}><strong>Proyecto:</strong> {event.Equipo?.Proyecto?.Nombre}</Text>
      <Text fz={11}><strong>Cliente:</strong> {event.Equipo?.Proyecto?.Cliente?.Nombre}</Text>
      <Text fz={11}><strong>Equipo:</strong> {event.Equipo?.Codigo}</Text>
      {incluirTecnico && (
        <Text fz={11}>
          <IconUserCog size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> 
          <strong> Técnico:</strong> {event.UsuarioAsignado?.NombreCompleto}
        </Text>
      )}
    </Stack>
  );

  // ========================== 🧾 AGENDA ==========================
  if (view === "agenda") {
    return <DatosEvento incluirTecnico={true} />;
  }

  // ========================== 🗓️ MES ==========================
  if (view === "month") {
    return (
      <HoverCard width={260} shadow="md" withinPortal openDelay={100}>
        <HoverCard.Target>
          <Stack gap={1} style={{ cursor: "pointer", color: "#0074d9" }}>
            <Text fz={11}><strong>{event.Id}</strong></Text>
            <Text fz={11} truncate><strong>{event.TipoTarea?.Nombre}</strong></Text>
            <Text fz={11} truncate><IconUserCog size={12} /> {event.UsuarioAsignado?.NombreCompleto}</Text>
          </Stack>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <DatosEvento incluirTecnico={true} />
        </HoverCard.Dropdown>
      </HoverCard>
    );
  }

  // ========================== 🗓️ WEEK / DAY ==========================
  if (view === "week" || view === "day") {
    return (
      <HoverCard width={260} shadow="md" withinPortal openDelay={100}>
        <HoverCard.Target>
          <div ref={ref} style={{ height: "100%", cursor: "pointer", color: "#0074d9", overflow: "hidden" }}>
            <Stack gap={1}>
              <Text fz={11}><strong>{event.Id}</strong></Text>
              {!mostrarSoloId && (
                <>
                  <Text fz={11}><strong>{event.TipoTarea?.Nombre}</strong> / {horaInicio} - {horaFin}</Text>
                  <Text fz={11}>{event.Equipo?.Codigo}</Text>
                </>
              )}
            </Stack>
          </div>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          {/* Técnico desactivado en Tooltip para Day/Week */}
          <DatosEvento incluirTecnico={false} />
        </HoverCard.Dropdown>
      </HoverCard>
    );
  }

  return <div>{event.title}</div>;
};

export default EventoPersonalizado;